import { useCallback, useEffect, useState } from 'react';
import { defaultStorageConfig } from '@/constants';
import { parseStoredValue, stringifyValue } from '@/utils';
function useSessionStorage(_a) {
    var key = _a.key, initialValue = _a.initialValue, _b = _a.config, config = _b === void 0 ? defaultStorageConfig : _b;
    // Get initial value from session storage or use initialValue
    var readValue = useCallback(function () {
        try {
            var item = window.sessionStorage.getItem(key);
            return parseStoredValue(item, initialValue, config, key);
        }
        catch (error) {
            console.warn("Error reading sessionStorage key \"".concat(key, "\":"), error);
            return initialValue;
        }
    }, [initialValue, key, config]);
    var _c = useState(readValue), storedValue = _c[0], setStoredValue = _c[1];
    // Return a wrapped version of useState's setter function
    var setValue = useCallback(function (value) {
        try {
            // Allow value to be a function for same API as useState
            var newValue = value instanceof Function ? value(storedValue) : value;
            // Save to session storage
            var valueToStore = stringifyValue(newValue, config, key);
            window.sessionStorage.setItem(key, valueToStore);
            // Save state
            setStoredValue(newValue);
            // Dispatch a custom event so other instances can update
            window.dispatchEvent(new StorageEvent('storage', {
                key: key,
                newValue: valueToStore,
                storageArea: sessionStorage,
            }));
        }
        catch (error) {
            console.warn("Error setting sessionStorage key \"".concat(key, "\":"), error);
        }
    }, [key, storedValue, config]);
    // Remove item from session storage
    var remove = useCallback(function () {
        try {
            window.sessionStorage.removeItem(key);
            setStoredValue(initialValue);
        }
        catch (error) {
            console.warn("Error removing sessionStorage key \"".concat(key, "\":"), error);
        }
    }, [key, initialValue]);
    useEffect(function () {
        setValue(readValue());
    }, []);
    // Handle storage changes from other windows/tabs
    useEffect(function () {
        var handleStorageChange = function (e) {
            if (e.key === key && e.storageArea === sessionStorage) {
                try {
                    var newValue = parseStoredValue(e.newValue, initialValue, config, key);
                    setStoredValue(newValue);
                }
                catch (error) {
                    console.warn("Error handling storage event for key \"".concat(key, "\":"), error);
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return function () { return window.removeEventListener('storage', handleStorageChange); };
    }, [key, initialValue, config]);
    return [storedValue, setValue, remove];
}
export default useSessionStorage;
